/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpResponse, HttpHeaders } from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { WebConfiguration as __Configuration } from '../web-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { DeliveryDocumentList } from '../models/delivery-document-list';

/**
 * All about the delivery documents
 */
@Injectable({
  providedIn: 'root',
})
class DeliveryDocumentsService extends __BaseService {
  static readonly listDeliveryDocumentsPath = '/deliveries/{uid}/documents';
  static readonly createDeliveryDocumentPath = '/deliveries/{uid}/documents';
  static readonly getDeliveryDocumentContentPath = '/deliveries/{uid}/documents/{documentUid}';
  static readonly deleteDeliveryDocumentPath = '/deliveries/{uid}/documents/{documentUid}';
  static readonly getDeliveryDocumentGetContentUrlPath = '/deliveries/{uid}/documents/{documentUid}/get';
  static readonly getDeliveryDocumentUploadContentUrlPath = '/deliveries/{uid}/documents/{documentUid}/put';
  static readonly getDeliveryDocumentDeleteContentUrlPath = '/deliveries/{uid}/documents/{documentUid}/delete';

  constructor(
    config: __Configuration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * List a delivery's documents
   *
   * List all the documents associated with a delivery
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  listDeliveryDocumentsResponse(uid: string): __Observable<__StrictHttpResponse<DeliveryDocumentList>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(uid))}/documents`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<DeliveryDocumentList>;
      })
    );
  }
  /**
   * List a delivery's documents
   *
   * List all the documents associated with a delivery
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  listDeliveryDocuments(uid: string): __Observable<DeliveryDocumentList> {
    return this.listDeliveryDocumentsResponse(uid).pipe(
      __map(_r => _r.body as DeliveryDocumentList)
    );
  }

  /**
   * Create a delivery document
   *
   * Create a delivery document and its associated file
   * @param params The `DeliveryDocumentsService.CreateDeliveryDocumentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `file`: File used to create a document
   *
   * - `role`: Role used to create a delivery document:
   *     * WEIGHBRIDGE_TICKET
   *     * LOAD_REPORT
   *     * DAILY_LOG
   *     * OTHER
   */
  createDeliveryDocumentResponse(params: DeliveryDocumentsService.CreateDeliveryDocumentParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let __formData = new FormData();
    __body = __formData;

    if (params.file != null) { __formData.append('file', params.file as string | Blob);}
    if (params.role != null) { __formData.append('role', params.role as string | Blob);}
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(params.uid))}/documents`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Create a delivery document
   *
   * Create a delivery document and its associated file
   * @param params The `DeliveryDocumentsService.CreateDeliveryDocumentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `file`: File used to create a document
   *
   * - `role`: Role used to create a delivery document:
   *     * WEIGHBRIDGE_TICKET
   *     * LOAD_REPORT
   *     * DAILY_LOG
   *     * OTHER
   */
  createDeliveryDocument(params: DeliveryDocumentsService.CreateDeliveryDocumentParams): __Observable<null> {
    return this.createDeliveryDocumentResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Get a delivery document's content
   *
   * Get the file content of a delivery document
   * @param params The `DeliveryDocumentsService.GetDeliveryDocumentContentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getDeliveryDocumentContentResponse(params: DeliveryDocumentsService.GetDeliveryDocumentContentParams): __Observable<__StrictHttpResponse<Blob>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(params.uid))}/documents/${encodeURIComponent(String(params.documentUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'blob'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Blob>;
      })
    );
  }
  /**
   * Get a delivery document's content
   *
   * Get the file content of a delivery document
   * @param params The `DeliveryDocumentsService.GetDeliveryDocumentContentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getDeliveryDocumentContent(params: DeliveryDocumentsService.GetDeliveryDocumentContentParams): __Observable<Blob> {
    return this.getDeliveryDocumentContentResponse(params).pipe(
      __map(_r => _r.body as Blob)
    );
  }

  /**
   * Delete a delivery document and its content
   *
   * Delete a delivery document and its associated file
   * @param params The `DeliveryDocumentsService.DeleteDeliveryDocumentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   */
  deleteDeliveryDocumentResponse(params: DeliveryDocumentsService.DeleteDeliveryDocumentParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(params.uid))}/documents/${encodeURIComponent(String(params.documentUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Delete a delivery document and its content
   *
   * Delete a delivery document and its associated file
   * @param params The `DeliveryDocumentsService.DeleteDeliveryDocumentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   */
  deleteDeliveryDocument(params: DeliveryDocumentsService.DeleteDeliveryDocumentParams): __Observable<null> {
    return this.deleteDeliveryDocumentResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Get a delivery document's get content pre-signed URL
   *
   * Get a pre-signed URL to fetch the delivery document's content directly from RustFS
   * @param params The `DeliveryDocumentsService.GetDeliveryDocumentGetContentUrlParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getDeliveryDocumentGetContentUrlResponse(params: DeliveryDocumentsService.GetDeliveryDocumentGetContentUrlParams): __Observable<__StrictHttpResponse<string>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(params.uid))}/documents/${encodeURIComponent(String(params.documentUid))}/get`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'text'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<string>;
      })
    );
  }
  /**
   * Get a delivery document's get content pre-signed URL
   *
   * Get a pre-signed URL to fetch the delivery document's content directly from RustFS
   * @param params The `DeliveryDocumentsService.GetDeliveryDocumentGetContentUrlParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getDeliveryDocumentGetContentUrl(params: DeliveryDocumentsService.GetDeliveryDocumentGetContentUrlParams): __Observable<string> {
    return this.getDeliveryDocumentGetContentUrlResponse(params).pipe(
      __map(_r => _r.body as string)
    );
  }

  /**
   * Get a delivery document's upload content pre-signed URL
   *
   * Get a pre-signed URL to upload the delivery document's content directly to RustFS
   * @param params The `DeliveryDocumentsService.GetDeliveryDocumentUploadContentUrlParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getDeliveryDocumentUploadContentUrlResponse(params: DeliveryDocumentsService.GetDeliveryDocumentUploadContentUrlParams): __Observable<__StrictHttpResponse<string>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(params.uid))}/documents/${encodeURIComponent(String(params.documentUid))}/put`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'text'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<string>;
      })
    );
  }
  /**
   * Get a delivery document's upload content pre-signed URL
   *
   * Get a pre-signed URL to upload the delivery document's content directly to RustFS
   * @param params The `DeliveryDocumentsService.GetDeliveryDocumentUploadContentUrlParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getDeliveryDocumentUploadContentUrl(params: DeliveryDocumentsService.GetDeliveryDocumentUploadContentUrlParams): __Observable<string> {
    return this.getDeliveryDocumentUploadContentUrlResponse(params).pipe(
      __map(_r => _r.body as string)
    );
  }

  /**
   * Get a delivery document's delete content pre-signed URL
   *
   * Get a pre-signed URL to delete the delivery document's content directly from RustFS
   * @param params The `DeliveryDocumentsService.GetDeliveryDocumentDeleteContentUrlParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getDeliveryDocumentDeleteContentUrlResponse(params: DeliveryDocumentsService.GetDeliveryDocumentDeleteContentUrlParams): __Observable<__StrictHttpResponse<string>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/deliveries/${encodeURIComponent(String(params.uid))}/documents/${encodeURIComponent(String(params.documentUid))}/delete`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'text'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<string>;
      })
    );
  }
  /**
   * Get a delivery document's delete content pre-signed URL
   *
   * Get a pre-signed URL to delete the delivery document's content directly from RustFS
   * @param params The `DeliveryDocumentsService.GetDeliveryDocumentDeleteContentUrlParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getDeliveryDocumentDeleteContentUrl(params: DeliveryDocumentsService.GetDeliveryDocumentDeleteContentUrlParams): __Observable<string> {
    return this.getDeliveryDocumentDeleteContentUrlResponse(params).pipe(
      __map(_r => _r.body as string)
    );
  }
}

namespace DeliveryDocumentsService {

  /**
   * Parameters for createDeliveryDocument
   */
  export interface CreateDeliveryDocumentParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * File used to create a document
     */
    file: Blob;

    /**
     * Role used to create a delivery document:
     *   * WEIGHBRIDGE_TICKET
     *   * LOAD_REPORT
     *   * DAILY_LOG
     *   * OTHER
     */
    role?: string;
  }

  /**
   * Parameters for getDeliveryDocumentContent
   */
  export interface GetDeliveryDocumentContentParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    documentUid: string;
  }

  /**
   * Parameters for deleteDeliveryDocument
   */
  export interface DeleteDeliveryDocumentParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    documentUid: string;
  }

  /**
   * Parameters for getDeliveryDocumentGetContentUrl
   */
  export interface GetDeliveryDocumentGetContentUrlParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    documentUid: string;
  }

  /**
   * Parameters for getDeliveryDocumentUploadContentUrl
   */
  export interface GetDeliveryDocumentUploadContentUrlParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    documentUid: string;
  }

  /**
   * Parameters for getDeliveryDocumentDeleteContentUrl
   */
  export interface GetDeliveryDocumentDeleteContentUrlParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    documentUid: string;
  }
}

export { DeliveryDocumentsService }
